/**
 * Site header entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS.
 *
 * Three jobs, all of them things CSS cannot do on its own:
 *
 * 1. The small-screen menu: flipping `aria-expanded`, closing the sheet on
 *    Escape, and dismissing it when the visitor clicks elsewhere. Above the
 *    desktop breakpoint the toggle is display:none, so none of it is reachable.
 * 2. The tone. The bar floats over the page rather than pushing it down, so
 *    what is behind it changes as the visitor scrolls — and white lettering
 *    disappears the moment a light section arrives underneath. Each dark
 *    section marks itself with `data-tone="dark"` on its own root; this reads
 *    whatever is actually beneath the bar and mirrors it. The header therefore
 *    knows no other block's name, and any section added later is handled by
 *    saying so on itself.
 * 3. The cart badge, when a page changes the cart without reloading. Same
 *    arrangement as the tone: the page announces `cart:changed` and hands over
 *    the answer the shop gave it, and the bar takes its own badge out of that.
 *    Nothing here knows which block did the changing.
 *
 * The glass itself is unchanged by scrolling — only its palette turns over.
 */

import './index.scss';

/** Everything the overlay can hand the keyboard, in document order. */
const FOCUSABLE =
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

const headers = document.querySelectorAll('.site-header');
const bars = document.querySelectorAll('.site-header__bar');

if (bars.length) {
    for (const bar of bars) {
        const toggle = bar.querySelector('.site-header__toggle');
        const panel = bar.querySelector('.site-header__panel');

        if (!toggle || !panel) {
            continue;
        }

        /**
         * Open or close the sheet, keeping the ARIA state and the attribute the
         * stylesheet reads in step.
         *
         * @param {boolean} open Desired state.
         * @returns {void}
         */
        const setOpen = (open) => {
            bar.setAttribute('data-open', String(open));
            toggle.setAttribute('aria-expanded', String(open));

            // The page behind must not scroll under a cover that fills it.
            document.documentElement.classList.toggle('has-menu-open', open);

            if (open) {
                /* Anything else covering the page steps aside; see the matching
                 * listener below for the case where this sheet is the one that
                 * has to. */
                document.dispatchEvent(
                    new CustomEvent('overlay:opened', { detail: { who: 'site-header' } })
                );

                /*
                 * Two frames, not one: the first paints the panel visible, and
                 * only after that will the browser move focus into something it
                 * had been treating as hidden.
                 */
                requestAnimationFrame(() =>
                    requestAnimationFrame(() => {
                        const first = panel.querySelector(FOCUSABLE);

                        if (first) {
                            first.focus();
                        }
                    })
                );
            }
        };

        setOpen(false);

        /* A modal dialog lives in the top layer and cannot be out-stacked, so
         * when one opens this sheet closes rather than sitting uselessly
         * underneath it. */
        document.addEventListener('overlay:opened', (event) => {
            if ('site-header' !== event.detail?.who) {
                setOpen(false);
            }
        });

        toggle.addEventListener('click', () => {
            setOpen('true' !== bar.getAttribute('data-open'));
        });

        /*
         * Escape closes and hands focus back, and Tab is kept inside while the
         * overlay is up: it covers the page, so anything the keyboard could
         * reach behind it is something the eye cannot follow.
         */
        bar.addEventListener('keydown', (event) => {
            if ('true' !== bar.getAttribute('data-open')) {
                return;
            }

            if ('Escape' === event.key) {
                setOpen(false);
                toggle.focus();
                return;
            }

            if ('Tab' !== event.key) {
                return;
            }

            const stops = [toggle, ...panel.querySelectorAll(FOCUSABLE)].filter(
                (node) => null !== node.offsetParent
            );

            if (!stops.length) {
                return;
            }

            const edge = event.shiftKey ? stops[0] : stops[stops.length - 1];

            if (document.activeElement === edge) {
                event.preventDefault();
                (event.shiftKey ? stops[stops.length - 1] : stops[0]).focus();
            }
        });

        /*
         * Above the breakpoint the panel is not a panel: `display: contents`
         * hands its children to the bar, so the dialog roles would be a lie
         * about a box that no longer exists. They are taken off there and put
         * back below it.
         */
        const desktop = window.matchMedia('(min-width: 80rem)');
        const setRoles = (isDesktop) => {
            if (isDesktop) {
                panel.removeAttribute('role');
                panel.removeAttribute('aria-modal');
                setOpen(false);
                return;
            }

            panel.setAttribute('role', 'dialog');
            panel.setAttribute('aria-modal', 'true');
        };

        desktop.addEventListener('change', (event) => setRoles(event.matches));
        setRoles(desktop.matches);
    }

    for (const header of headers) {
        /*
         * Every header reads its tone, floating or not. The one that sits in
         * the flow finds the page's own ground beneath it and comes back
         * light — which is the answer it should always have given, rather than
         * painting itself ink and taking the menu with it.
         */
        const bar = header.querySelector('.site-header__bar');

        if (!bar) {
            continue;
        }

        /**
         * Take the tone of whatever the bar is currently over.
         *
         * Sampling the stack at a point is what makes this general: it asks the
         * page what is actually there rather than tracking offsets, so it stays
         * right through sticky sections, anchors and any layout added later.
         *
         * @returns {void}
         */
        const readTone = () => {
            const box = bar.getBoundingClientRect();
            const beneath = document
                .elementsFromPoint(
                    Math.round(box.left + box.width / 2),
                    Math.round(box.top + box.height / 2)
                )
                .find((node) => !header.contains(node));
            const toned = beneath ? beneath.closest('[data-tone]') : null;

            header.setAttribute('data-tone', toned ? toned.getAttribute('data-tone') : 'light');
        };

        // Scroll fires far more often than the page paints, so the read is
        // deferred to the frame that will actually show the result.
        let queued = false;
        const schedule = () => {
            if (queued) {
                return;
            }

            queued = true;
            requestAnimationFrame(() => {
                queued = false;
                readTone();
            });
        };

        document.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule, { passive: true });

        /* With smooth scrolling on, src/js/motion.js announces each move from
         * inside the frame it happens in. Reading then, rather than a frame
         * later, is what keeps the bar from showing the old tone for one frame
         * as it crosses from a dark section into a light one. */
        window.addEventListener('theme:scroll', readTone);
        readTone();
    }
}

/*
 * The cart page saves a quantity without reloading — it posts the form and
 * swaps in the line and the totals — so the number in the bar would sit at
 * whatever it was when the page was drawn. Whoever does that says so, and
 * passes the page the shop answered with; the badge is read out of it here.
 *
 * The event carries a document rather than a number so that no other block has
 * to know what the bar's badge is called, and so the count arrives already
 * rendered by the same code that renders it on a full page load. No request is
 * made: this is the answer that block already had in its hands.
 */
const badge = document.querySelector('.site-header__utility-count');

if (badge) {
    const label = badge
        .closest('.site-header__utility')
        ?.querySelector('.site-header__utility-label');

    const utility = badge.closest('.site-header__utility');

    /**
     * Set the badge and the name of the link it sits in.
     *
     * @param {number} count How many pieces are in the cart now.
     */
    const tell = (count) => {
        badge.dataset.count = String(count);
        badge.textContent = count > 99 ? '99+' : String(count);

        if (!label || !utility) {
            return;
        }

        if (count < 1) {
            label.textContent = utility.dataset.labelEmpty ?? label.textContent;
            return;
        }

        const words = count === 1 ? utility.dataset.labelOne : utility.dataset.labelMany;

        label.textContent = (words ?? '').replace('%s', String(count));
    };

    document.addEventListener('cart:changed', (event) => {
        /*
         * Two shapes, because two blocks say this and they have different
         * things in their hands. The cart page has the page the shop sent back
         * and the badge is already drawn in it; the drawer has only a number.
         */
        const count = event.detail?.count;

        if (typeof count === 'number') {
            tell(count);
            return;
        }

        const page = event.detail?.page;

        if (typeof page?.querySelector !== 'function') {
            return;
        }

        const fresh = page.querySelector('.site-header__utility-count');

        if (!fresh) {
            return;
        }

        /*
         * Copied rather than swapped in. Replacing the element would be fewer
         * lines, but it sits inside the link the reader may be on, and taking a
         * focused subtree out from under somebody moves the keyboard back to
         * the top of the page.
         */
        badge.dataset.count = fresh.dataset.count ?? '0';
        badge.textContent = fresh.textContent;

        const next = page.querySelector('.site-header__utility--bag .site-header__utility-label');

        /* The hidden label is the link's whole accessible name, count and all,
         * so it goes stale in exactly the same way the badge does. */
        if (label && next) {
            label.textContent = next.textContent;
        }
    });
}
